import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import ActionsContext from "common/ActionsContext";
import Request from "common/Request";
import { AddBqnModal } from "./AddBqnModal";
import BqnContext from "common/BqnContext";
import Table from "common/graphs/Table/Table";
import { NumberCell } from "common/graphs/Table/TableRenders";
import { parseHostnames } from "common/api";
import Modal from "common/Modal";

const getHostnames = () =>
  ifCl
    .run("show system hostname detail")
    .then((response) => parseHostnames(response));

const ResultsLayout = styled.div`
  flex: 100%;
  overflow: hidden; /*list will scroll*/
`;

const TableContainer = styled.div`
  width: 100%;
  overflow: auto;
  padding-top: 0px;
`;

const DeleteModalWrapper = styled.div`
  ul {
    padding: 0 0 0 15px;
    margin: -20px 0 20px 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 20px;

    li {
      margin: 3px 0 !important;
    }
  }
`;

const DeleteModal = ({
  showDeleteModal,
  setShowDeleteModal,
  items = [],
  whenDone,
  loginBqnId,
}) => {
  const handleApply = () => {
    const cmds = items.map((item) => `clear system bqn id ${item.bqnId} force`);
    ifCl
      .run(`${cmds.join("\n")}\n`, /*is batch*/ true)
      .then(() => {
        setShowDeleteModal(false);
        if (items.some((item) => parseFloat(loginBqnId) === parseFloat(item.bqnId))) {
          login.doLogout();
        } else {
          whenDone();
        }
      })
      .catch((error) => window.showModalError && window.showModalError(error, ""));
  };

  if (!showDeleteModal) {
    return null;
  }

  const title =
    items.length === 1
      ? `Eliminate BQN ID ${items[0].bqnId} with IP address ${items[0].address} from this cluster?`
      : `Eliminate ${items.length} BQNs from this cluster?`;

  return (
    <Modal
      title={title}
      onClose={() => setShowDeleteModal(false)}
      closeLabel="CANCEL"
      superIcon={"delete_forever"}
      large={items.length > 1 && items.length < 100}
      content={() => (
        <DeleteModalWrapper>
          {items.length > 1 && items.length < 100 ? (
            <ul>
              {items.map((item) => (
                <li key={item.bqnId}>BQN ID {item.bqnId}</li>
              ))}
            </ul>
          ) : null}
        </DeleteModalWrapper>
      )}
      applyLabel="YES"
      onApply={handleApply}
    />
  );
};

const BqnList = () => {
  const [request, setRequest] = useState(null);
  const [showAddBqnModal, setShowAddBqnModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteSelected, setDeleteSelected] = useState([]);
  const [selected, setSelected] = useState([]);
  const actions = useContext(ActionsContext);
  const { loginBqnId, loadClusterStatus, bqns } = useContext(BqnContext);

  useEffect(() => {
    doLoad();
  }, [bqns]);

  useEffect(() => actions.recv("add-bqn", () => setShowAddBqnModal(true)), []);

  const doLoad = () => setRequest(doRequest());

  const doRequest = () =>
    getHostnames().then((hostnamesRes) => {
      const response = bqns.reduce((acc, bqn, index) => {
        const {
          bqnId,
          ipAddress,
          function: functionBqn,
          onlChanges,
          onlChanged,
          sshPort,
        } = bqn;
        const hostname = hostnamesRes[bqnId] || "n/a";
        const newEntry = {
          id: index,
          BQN: bqnId,
          ADDRESS: ipAddress,
          FUNCTION: functionBqn,
          "ONL-CHANGES": onlChanges,
          "ONL-CHANGED": onlChanged,
          HOSTNAME: hostname,
          "SSH-PORT": sshPort,
        };
        acc.push(newEntry);
        return acc;
      }, []);

      return { response };
    });

  const getColumns = () => {
    return [
      {
        label: "BQN ID",
        id: "BQN",
        numeric: false,
        title: "BQN ID",
        parse: (value) => parseFloat(value),
      },
      {
        label: "HOSTNAME",
        id: "HOSTNAME",
        numeric: false,
        title: "HOSTNAME",
      },
      {
        label: "IP-ADDRESS",
        id: "ADDRESS",
        numeric: false,
        title: "IP Address",
      },
      {
        label: "SSH-PORT",
        id: "SSH-PORT",
        numeric: true,
        title: "SSH PORT",
      },
      {
        label: "FUNCTION",
        id: "FUNCTION",
        numeric: false,
        title: "FUNCTION",
      },
      {
        label: "ONL-CHANGES",
        id: "ONL-CHANGES",
        numeric: true,
        title: "ONL-CHANGES",
        renderCell: (value) => (
          <NumberCell value={value} hasNA={false} precision={0} />
        ),
      },
      {
        label: "ONL-CHANGED",
        id: "ONL-CHANGED",
        numeric: false,
        title: "ONL-CHANGED",
      },
      {
        label: "ACTIONS",
        id: "ACTIONS",
        actions: [
          {
            label: "Delete",
            icon: "delete",
            onClick: (row) => {
              setDeleteSelected([
                { bqnId: row["BQN"], address: row["ADDRESS"] },
              ]);
              setShowDeleteModal(true);
            },
          },
        ],
      },
    ];
  };

  return request === null ? null : (
    <>
      <ResultsLayout>
        <>
          <TableContainer>
            <Request during={request}>
              {({ response }) => {
                if (response.length === 0) {
                  return null;
                }

                return (
                  <Table
                    isSelectable={true}
                    isRowSelectable={false}
                    data={response}
                    sort={true}
                    columns={getColumns()}
                    search={true}
                    csvPrefix="cluster-configuration"
                    rowsPerPageNr={50}
                    selected={selected}
                    setSelected={setSelected}
                    deleteSelected={(values) => {
                      const selectedItems = response
                        .filter((row) => values.includes(row.id))
                        .map((row) => ({
                          bqnId: row["BQN"],
                          address: row["ADDRESS"],
                        }));
                      setDeleteSelected(selectedItems);
                      setShowDeleteModal(true);
                    }}
                  />
                );
              }}
            </Request>
          </TableContainer>
          <DeleteModal
            showDeleteModal={showDeleteModal}
            setShowDeleteModal={setShowDeleteModal}
            items={deleteSelected}
            loginBqnId={loginBqnId}
            whenDone={() => {
              setSelected([]);
              loadClusterStatus();
            }}
          />
          {showAddBqnModal && (
            <AddBqnModal
              onOk={() => loadClusterStatus()}
              onClose={() => setShowAddBqnModal(false)}
            />
          )}
        </>
      </ResultsLayout>
    </>
  );
};

export default BqnList;
